import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);

  const relatedComics = useMemo(() => {
    if (!comic) return [];
    return comics
      .filter((c) => c.categorySlug === comic.categorySlug && c.id !== comic.id)
      .slice(0, 4);
  }, [comic]);

  if (!comic) {
    return (
      <>
        <SEOHead title="漫画未找到 - 韩国漫画网" description="您查找的漫画不存在或已被移除。" />
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <span className="w-20 h-20 mx-auto flex items-center justify-center bg-background-200 rounded-full mb-4">
              <i className="ri-file-unknow-line text-4xl text-foreground-400"></i>
            </span>
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">您查找的漫画不存在或已被移除</p>
            <Link to="/" className="inline-block px-6 py-2.5 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const cat = categories.find((c) => c.slug === comic.categorySlug);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: cat?.name || comic.category, href: `/category/${comic.categorySlug}` },
    { label: comic.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩国漫画网`,
    description: comic.description,
    image: comic.cover,
    author: {
      '@type': 'Person',
      name: comic.author.name,
    },
    datePublished: comic.createdAt,
    dateModified: comic.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: '韩国漫画网',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.koreanmanhwa.cn/article/${comic.slug}`,
    },
  };

  return (
    <>
      <SEOHead
        title={`${comic.title} - 韩国漫画网`}
        description={comic.description}
        keywords={`${comic.title},${comic.category},韩国漫画,免费在线韩漫,${comic.tags.join(',')}`}
        canonicalUrl={`https://www.koreanmanhwa.cn/article/${comic.slug}`}
        ogType="article"
        ogImage={comic.cover}
        schema={articleSchema}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        {/* Banner */}
        <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden" aria-label={`${comic.title} 横幅`}>
          <img
            src={comic.banner}
            alt={`${comic.title} 横幅背景`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/40 to-transparent" />
        </section>

        <div className="max-w-7xl mx-auto px-4 md:px-6 -mt-32 relative z-10">
          <div className="py-4">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="flex flex-col md:flex-row gap-6 md:gap-5">
            {/* Cover */}
            <aside className="w-full md:w-[280px] flex-shrink-0">
              <div className="rounded-xl overflow-hidden bg-background-50 border border-background-200/60">
                <div className="aspect-[3/4] overflow-hidden">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} 漫画封面`}
                    title={`${comic.title} - 韩国漫画网免费在线韩漫`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </aside>

            {/* Info */}
            <article className="flex-1 min-w-0" itemScope itemType="https://schema.org/Article">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {comic.isHot && (
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-primary-500 text-background-50 whitespace-nowrap">HOT</span>
                )}
                {comic.isNew && (
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-accent-500 text-background-50 whitespace-nowrap">NEW</span>
                )}
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 whitespace-nowrap">
                  {comic.category}
                </span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                  comic.status === '连载中'
                    ? 'bg-secondary-100 text-secondary-700'
                    : 'bg-foreground-100 text-foreground-700'
                }`}>
                  {comic.status}
                </span>
              </div>

              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-2" itemProp="headline">
                {comic.title}
              </h1>
              {comic.subtitle && (
                <p className="text-sm text-foreground-500 mb-4">{comic.subtitle}</p>
              )}

              <div className="flex flex-wrap items-center gap-4 md:gap-5 mb-5 text-sm">
                <div className="flex items-center gap-1">
                  <span className="text-accent-600 font-bold text-lg">{comic.rating}</span>
                  <div className="flex items-center">
                    {Array.from({ length: 5 }, (_, i) => (
                      <span key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className={`text-sm ${i < Math.round(comic.rating) ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}></i>
                      </span>
                    ))}
                  </div>
                  <span className="text-foreground-400">({comic.ratingCount.toLocaleString()}人评分)</span>
                </div>
                <span className="text-foreground-400">
                  <strong className="text-foreground-700">{(comic.totalViews / 10000).toFixed(0)}万</strong> 阅读
                </span>
                <span className="text-foreground-400">
                  <strong className="text-foreground-700">{(comic.totalFavorites / 10000).toFixed(0)}万</strong> 收藏
                </span>
                <span className="text-foreground-400">
                  共 <strong className="text-foreground-700">{comic.totalChapters}</strong> 话
                </span>
              </div>

              <div className="flex items-center gap-1 mb-4 text-sm text-foreground-500">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-user-line"></i></span>
                <span>作者：<strong itemProp="author" className="text-foreground-800">{comic.author.name}</strong></span>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-5">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full text-xs bg-background-200 text-foreground-600 whitespace-nowrap">
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="flex flex-wrap gap-3 mb-6">
                <button className="px-8 py-2.5 bg-primary-500 text-background-50 rounded-full font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                  开始阅读
                </button>
                <button className="px-6 py-2.5 border border-background-300 text-foreground-700 rounded-full font-medium text-sm hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-bookmark-line"></i></span>
                  加入收藏
                </button>
                <button className="px-6 py-2.5 border border-background-300 text-foreground-700 rounded-full font-medium text-sm hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-share-forward-line"></i></span>
                  分享
                </button>
              </div>

              <div className="bg-background-100 rounded-xl p-4 md:p-5 mb-6" itemProp="description">
                <h3 className="font-semibold text-foreground-900 mb-2 text-sm">作品简介</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.longDescription}</p>
              </div>
            </article>
          </div>

          {/* Chapters */}
          <section className="mt-10 md:mt-14 mb-10" aria-label="章节列表">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center bg-primary-100 rounded-lg text-primary-600">
                  <i className="ri-list-check"></i>
                </span>
                <h2 className="font-heading text-xl font-bold text-foreground-900">
                  <a href="#chapter-list">章节列表</a>
                </h2>
              </div>
              <span className="text-sm text-foreground-400">共 {comic.totalChapters} 话</span>
            </div>
            <div className="bg-background-50 border border-background-200/60 rounded-xl overflow-hidden">
              <div className="divide-y divide-background-200/60">
                {comic.chapters.map((ch) => (
                  <div
                    key={ch.id}
                    className="flex items-center justify-between px-4 md:px-5 py-3 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-sm font-medium text-foreground-900 truncate">{ch.title}</span>
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0">
                      <span className="text-xs text-foreground-400">{ch.updatedAt}</span>
                      <span className="text-xs text-foreground-400 flex items-center gap-1">
                        <span className="w-3 h-3 flex items-center justify-center"><i className="ri-eye-line text-[10px]"></i></span>
                        {(ch.viewCount / 10000).toFixed(1)}万
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              {comic.totalChapters > 20 && (
                <div className="px-4 py-3 text-center border-t border-background-200/60">
                  <button className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer">
                    查看更多章节
                  </button>
                </div>
              )}
            </div>
          </section>

          {/* Related */}
          {relatedComics.length > 0 && (
            <section className="mb-10 md:mb-14" aria-label="相关推荐">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-7 h-7 flex items-center justify-center bg-accent-100 rounded-lg text-accent-600">
                  <i className="ri-thumb-up-line"></i>
                </span>
                <h2 className="font-heading text-xl font-bold text-foreground-900">
                  <a href="#related-section">相关推荐</a>
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}